<template>
  <div>
    <form
      id="contact"
      name="Contact"
      class="max-w-md"
      data-netlify="true"
      data-netlify-honeypot="bot-field"
      novalidate
      @submit.prevent="submitForm"
    >
      <input type="hidden" name="form-name" value="Contact" />

      <div class="mb-5">
        <FormInput
          id="name"
          v-model="fields.name"
          name="name"
          :placeholder="$t('form.name.placeholder')"
          :label="$t('form.name.label')"
          :isError="v$.name.$error"
          @change="v$.name.$touch"
        />
      </div>

      <div class="mb-5">
        <FormInput
          id="email"
          v-model="fields.email"
          :placeholder="$t('form.email.placeholder')"
          name="email"
          :label="$t('form.email.label')"
          type="email"
          :isError="v$.email.$error"
          @change="v$.email.$touch"
        />
      </div>

      <div class="mb-5">
        <FormTextarea
          id="message"
          v-model="fields.message"
          class="min-h-[140px]"
          name="message"
          :placeholder="$t('form.message.placeholder')"
          :label="$t('form.message.label')"
          :isError="v$.message.$error"
          @change="v$.message.$touch"
        />
      </div>

      <!-- Recaptcha -->
      <div class="mb-5">
        <FormField :isError="v$.recaptchaValue.$error">
          <ClientOnly>
            <RecaptchaV2
              @widget-id="onWidgetId"
              @load-callback="onRecaptchaVerify"
              @expired-callback="onRecaptchaExpired"
            />
          </ClientOnly>
        </FormField>
      </div>

      <div>
        <Button type="submit">{{ $t('form.contactUs') }}</Button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import useVuelidate from '@vuelidate/core'
import { required, email } from '@vuelidate/validators'
import { RecaptchaV2 } from 'vue3-recaptcha-v2'

// Form state
const fields = reactive({
  name: '',
  email: '',
  message: '',
  recaptchaValue: ''
})

// Vuelidate rules
const rules = {
  name: { required },
  email: { required, email },
  message: { required },
  recaptchaValue: { required }
}

const emit = defineEmits<{
  (event: 'submitted'): void
}>()

const v$ = useVuelidate(rules, fields)

function onRecaptchaVerify(response: string) {
  fields.recaptchaValue = response
}

function onRecaptchaExpired() {
  fields.recaptchaValue = ''
}

// Helper to encode Netlify form
function encode(data: any) {
  return Object.keys(data)
    .map((k) => encodeURIComponent(k) + '=' + encodeURIComponent(data[k]))
    .join('&')
}

async function submitForm() {
  const isFormInvalid = await v$.value.$validate()

  if (!isFormInvalid) {
    return
  }

  await fetch('/', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    body: encode({
      'form-name': 'Contact',
      ...fields
    })
  })
    .then(() => {
      console.log('Submitted w/ data:', fields)
      emit('submitted')
    })
    .catch((e) => {
      console.error(e)
    })

  const payload = {
    email_address: fields.email,
    status: 'subscribed'
  }

  const { error } = await useFetch('/.netlify/functions/mailchimp-form', {
    headers: { 'Content-type': 'application/json' },
    method: 'POST',
    body: toRaw(payload)
  })

  if (error.value) {
    console.log('error:', error.value)
  }
}
</script>
